import { recordedTime } from '@/utils/recordedValues'
/**
 * Audit Store - Zustand
 * State management for Audit Log viewing
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { AuditLogEntry, AuditLogFilters, AuditAction } from '@/types/session.types'
import { DEFAULT_AUDIT_LOG_FILTERS } from '@/types/session.types'
import { getAuditService } from '@/services/audit'

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface AuditState {
  // Data
  entries: AuditLogEntry[]
  selectedEntry: AuditLogEntry | null
  isLoading: boolean
  error: string | null

  // Filters
  filters: AuditLogFilters

  // Pagination
  page: number
  pageSize: number

  // Stats
  stats: {
    totalEntries: number
    successRate: number
    byAction: Array<{ action: AuditAction; count: number }>
    recentFailures: number
  } | null

  loginStats: {
    totalLogins: number
    failedLogins: number
    uniqueUsers: number
    topLocations: Array<{ city: string; count: number }>
  } | null
}

interface AuditActions {
  // Load operations
  loadAuditLog: () => Promise<void>
  loadUserAuditLog: (userId: string) => Promise<void>
  loadRecentLogins: (limit?: number) => Promise<void>
  loadStats: () => Promise<void>

  // Selection
  setSelectedEntry: (entryId: string | null) => void

  // Filters
  setFilters: (filters: Partial<AuditLogFilters>) => void
  clearFilters: () => void
  setSearchQuery: (query: string) => void

  // Pagination
  setPage: (page: number) => void
  setPageSize: (size: number) => void

  // Computed
  getFilteredEntries: () => AuditLogEntry[]
  getPaginatedEntries: () => {
    items: AuditLogEntry[]
    total: number
    page: number
    pageSize: number
    totalPages: number
  }

  // Reset
  resetStore: () => void
}

type AuditStore = AuditState & AuditActions

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: AuditState = {
  entries: [],
  selectedEntry: null,
  isLoading: false,
  error: null,
  filters: DEFAULT_AUDIT_LOG_FILTERS,
  page: 1,
  pageSize: 20,
  stats: null,
  loginStats: null,
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useAuditStore = create<AuditStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ========================================
      // LOAD OPERATIONS
      // ========================================

      loadAuditLog: async () => {
        set({ isLoading: true, error: null })
        try {
          const entries = await getAuditService().listAll()
          set({ entries, isLoading: false })
        } catch (err) {
          set({
            isLoading: false,
            error: err instanceof Error ? err.message : 'Failed to load audit log',
          })
        }
      },

      loadUserAuditLog: async (userId: string) => {
        set({ isLoading: true, error: null })
        try {
          const entries = await getAuditService().listForUser(userId)
          set({ entries, isLoading: false })
        } catch (err) {
          set({
            isLoading: false,
            error: err instanceof Error ? err.message : 'Failed to load audit log',
          })
        }
      },

      loadRecentLogins: async (limit = 10) => {
        set({ isLoading: true, error: null })
        try {
          const entries = await getAuditService().listRecentLogins(limit)
          set({ entries, isLoading: false })
        } catch (err) {
          set({
            isLoading: false,
            error: err instanceof Error ? err.message : 'Failed to load logins',
          })
        }
      },

      loadStats: async () => {
        const { stats, loginStats } = await getAuditService().getStats()
        set({ stats, loginStats })
      },

      // ========================================
      // SELECTION
      // ========================================

      setSelectedEntry: (entryId: string | null) => {
        if (!entryId) {
          set({ selectedEntry: null })
          return
        }
        const entry = get().entries.find(e => e.id === entryId) || null
        set({ selectedEntry: entry })
      },

      // ========================================
      // FILTERS
      // ========================================

      setFilters: (newFilters: Partial<AuditLogFilters>) => {
        set(state => ({
          filters: { ...state.filters, ...newFilters },
          page: 1,
        }))
      },

      clearFilters: () => {
        set({ filters: DEFAULT_AUDIT_LOG_FILTERS, page: 1 })
      },

      setSearchQuery: (query: string) => {
        set(state => ({
          filters: { ...state.filters, searchQuery: query },
          page: 1,
        }))
      },

      // ========================================
      // PAGINATION
      // ========================================

      setPage: (page: number) => {
        set({ page })
      },

      setPageSize: (pageSize: number) => {
        set({ pageSize, page: 1 })
      },

      // ========================================
      // COMPUTED
      // ========================================

      getFilteredEntries: () => {
        const { entries, filters } = get()

        let filtered = [...entries]

        // Filter by action
        if (filters.action && filters.action !== 'all') {
          filtered = filtered.filter(e => e.action === filters.action)
        }

        // Filter by resource type
        if (filters.resourceType && filters.resourceType !== 'all') {
          filtered = filtered.filter(e => e.resourceType === filters.resourceType)
        }

        // Filter by success
        if (filters.success !== undefined && filters.success !== 'all') {
          filtered = filtered.filter(e => e.success === filters.success)
        }

        // Filter by user
        if (filters.userId) {
          filtered = filtered.filter(e => e.userId === filters.userId)
        }

        // Filter by date range
        if (filters.dateFrom) {
          const from = new Date(filters.dateFrom).getTime()
          filtered = filtered.filter(e => (recordedTime(e.createdAt) ?? NaN) >= from)
        }
        if (filters.dateTo) {
          const to = new Date(filters.dateTo).getTime()
          filtered = filtered.filter(e => (recordedTime(e.createdAt) ?? NaN) <= to)
        }

        // Search in details
        if (filters.searchQuery?.trim()) {
          const query = filters.searchQuery.toLowerCase()
          filtered = filtered.filter(
            e =>
              e.action.toLowerCase().includes(query) ||
              e.userEmail?.toLowerCase().includes(query) ||
              e.performedByName?.toLowerCase().includes(query) ||
              e.resourceName?.toLowerCase().includes(query) ||
              JSON.stringify(e.details ?? null)
                .toLowerCase()
                .includes(query)
          )
        }

        // Already sorted by date in mock data
        return filtered
      },

      getPaginatedEntries: () => {
        const { page, pageSize } = get()
        const filtered = get().getFilteredEntries()
        const total = filtered.length
        const totalPages = Math.ceil(total / pageSize)
        const startIndex = (page - 1) * pageSize
        const items = filtered.slice(startIndex, startIndex + pageSize)

        return { items, total, page, pageSize, totalPages }
      },

      // ========================================
      // RESET
      // ========================================

      resetStore: () => {
        set(initialState)
      },
    }),
    { name: 'AuditStore' }
  )
)

// ============================================================================
// SELECTORS
// ============================================================================

export const selectEntries = (state: AuditStore) => state.entries
export const selectSelectedEntry = (state: AuditStore) => state.selectedEntry
export const selectIsLoading = (state: AuditStore) => state.isLoading
export const selectFilters = (state: AuditStore) => state.filters
export const selectPage = (state: AuditStore) => state.page
export const selectPageSize = (state: AuditStore) => state.pageSize
export const selectStats = (state: AuditStore) => state.stats
export const selectLoginStats = (state: AuditStore) => state.loginStats
